import * as FerroBox  from 'ferrobox-core'
import {DEPLOY_INFO} from "./constants";
import {Base64} from 'js-base64';
import {sha512} from '@noble/hashes/sha2.js';
import {Buffer} from 'buffer'

const VERIFY_HOSTNAME = false

export type DownloadResult = {
    success: true,
    stream: ReadableStream,
    meta: FerroBox.types.MetaInV1Base,
} | {
    success: false,
    error: string,
}

const disallowedPathType: readonly string[] = [
    'static'
] as const

const allowedPathType: readonly string[] = [
    'f', 'file',
    'p', 'plaintext',
    'm', 'markdown',
] as const

function isPathValid(pathType: string): boolean {
    return allowedPathType.includes(pathType) && !disallowedPathType.includes(pathType)
}

/**
 * 显示错误信息（覆盖整个页面）
 */
export function showError(message: string): void {
    const span = document.createElement('span')
    span.textContent = message

    document.body.innerHTML = `
        <div style="
            font-family: Arial, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            background-color: #f5f5f5;
        ">
            <div style="
                background-color: white;
                padding: 20px;
                border-radius: 8px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                max-width: 500px;
                text-align: center;
            ">
                <h2 style="color: #d32f2f;">Download Error</h2>
                <p>${span.innerHTML}</p>
                <button onclick="history.back()" style="
                    margin-top: 15px;
                    padding: 10px 20px;
                    background-color: #1976d2;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                ">Go Back</button>
            </div>
        </div>
    `;
}

export interface VerifyResult {
    valid: boolean;
    actualSize: number;
    actualHash: string;
    expectedSize: number;
    expectedHash: string;
}

/**
 * 边读流边增量计算 SHA-512 和 size，完成后一次性比对预期值。
 *
 * @returns VerifyResult（actualHash 为 base64 格式，与 MetaInV1Base.hash 一致）
 */
export async function verifyStream(
    stream: ReadableStream<Uint8Array>,
    expectedHash: string,
    expectedSize: number,
): Promise<VerifyResult> {
    const verifier = sha512.create();
    const expectedHashBytes = Base64.toUint8Array(expectedHash);

    const reader = stream.getReader();
    let actualSize = 0;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        verifier.update(value);
        actualSize += value.length;
    }

    const actualHashBytes = verifier.digest();

    return {
        valid: actualSize === expectedSize && Buffer.compare(actualHashBytes, expectedHashBytes) === 0,
        actualSize,
        actualHash: Base64.fromUint8Array(actualHashBytes),
        expectedSize,
        expectedHash,
    };
}

const INTEGRITY_WARNING_OVERLAY_ID = 'integrity-warning'
const INTEGRITY_DETAILS_ID = 'integrity-details'
const BTN_DISCARD_ID = 'btn-discard'
const BTN_KEEP_ID = 'btn-keep'

/**
 * 显示完整性警告对话框，返回用户是否选择保留/继续。
 *
 * 对话框从 DOM 中读取指定 ID 的元素：
 *   - overlay 容器（id="{INTEGRITY_WARNING_OVERLAY_ID}"）
 *   - 详情容器（id="{INTEGRITY_DETAILS_ID}"）——会被填充实际值
 *   - 丢弃按钮（id="{BTN_DISCARD_ID}"）
 *   - 保留按钮（id="{BTN_KEEP_ID}"）
 *
 * 如果 DOM 元素缺失，默认返回 false（丢弃/取消）。
 */
export function showIntegrityWarning(result: VerifyResult): Promise<boolean> {
    return new Promise((resolve) => {
        const overlay = document.getElementById(INTEGRITY_WARNING_OVERLAY_ID);
        if (!overlay) {
            resolve(false);
            return;
        }

        const detailsContainer = document.getElementById(INTEGRITY_DETAILS_ID)!;
        detailsContainer.innerHTML = `
            <div class="detail-item">Expected size: ${result.expectedSize} bytes</div>
            <div class="detail-item">Actual size: ${result.actualSize} bytes</div>
            <div class="detail-item">Expected hash: ${result.expectedHash}</div>
            <div class="detail-item">Actual hash: ${result.actualHash}</div>
        `;

        overlay.style.display = 'flex';

        const discardBtn = document.getElementById(BTN_DISCARD_ID)!;
        const keepBtn = document.getElementById(BTN_KEEP_ID)!;

        const cleanup = () => {
            overlay.style.display = 'none';
            discardBtn.removeEventListener('click', onDiscard);
            keepBtn.removeEventListener('click', onKeep);
        };

        const onDiscard = () => {
            cleanup();
            resolve(false);
        };

        const onKeep = () => {
            cleanup();
            resolve(true);
        };

        discardBtn.addEventListener('click', onDiscard);
        keepBtn.addEventListener('click', onKeep);
    });
}

export async function downloadFromUrl(url: URL | Location): Promise<DownloadResult> {
    if (VERIFY_HOSTNAME && url.hostname !== DEPLOY_INFO.download.webHost) {
        return {
            success: false,
            error: 'Hostname does not match expected webHost'
        }
    }
    const [pathType, slug, waste] = url.pathname.substring(1).split('/', 3)
    if (!isPathValid(pathType) || waste) {
        return {
            success: false,
            error: 'Invalid path type or slug'
        }
    }
    try {
        const key: Uint8Array = Buffer.from(url.hash.substring(1), 'base64')
        const meta = await fetchMeta(slug)
        // use default download options for Data
        const result = await FerroBox.download(meta, key)
        return {
            success: true,
            stream: result.stream,
            meta: result.meta,
        }
    } catch (error) {
        return {
            success: false,
            error: String(error)
        }
    }
}

async function fetchMeta(slug: string): Promise<Parameters<typeof FerroBox.download>[0]> {
    const url = new URL(slug, DEPLOY_INFO.download.endpoints.meta)
    const response = await fetch(url)
    if (!response.ok) {
        throw new Error(`Failed to fetch metadata: ${response.status} ${response.statusText}`)
    }
    return await response.json()
}

// ===== Shared text rendering utilities (for plaintext & markdown) =====

/**
 * 检测 MIME 类型是否为 text/*（可用于 text/plain、text/markdown 等）
 */
export function isTextMime(mime: string | undefined): boolean {
    if (mime === undefined || mime === '') {
        return false;
    }
    return mime.startsWith('text/');
}

/**
 * 专门检测是否为 text/markdown
 */
export function isMarkdownMime(mime: string | undefined): boolean {
    if (mime === undefined || mime === '') {
        return false;
    }
    return mime.startsWith('text/markdown');
}

/** 所有 TextDecoder 合法的 UTF-8 编码标签（不区分大小写） */
const UTF8_LABELS = new Set([
    'unicode-1-1-utf-8',
    'utf-8',
    'utf8',
]);

/**
 * 从 MIME Content-Type 中提取 charset / encoding 参数。
 * 例如 "text/plain; charset=utf-8" → "utf-8"
 * 如果没有，返回 undefined。
 */
export function extractEncoding(mime: string): string | undefined {
    const match = /;\s*charset\s*=\s*([^\s;]+)/i.exec(mime);
    return match ? match[1] : undefined;
}

/**
 * 判断是否为 UTF-8 编码（或其合法的别名）。
 */
export function isUtf8(label: string): boolean {
    return UTF8_LABELS.has(label.toLowerCase());
}

/**
 * 获取可用的 TextDecoder 编码标签。
 * 若 label 在当前环境中被 TextDecoder 支持，原样返回；
 * 否则回退到 'utf-8' 并提示服务端指定的编码不受支持。
 */
export function resolveEncoding(label: string): string {
    try {
        new TextDecoder(label);
        return label;
    } catch {
        console.warn(`Unsupported encoding "${label}", falling back to utf-8.`);
        return 'utf-8';
    }
}

// ---- Warning dialog helpers (generic pattern) ----

interface WarningDialogElements {
    overlay: HTMLElement;
    detailsContainer: HTMLElement;
    cancelBtn: HTMLElement;
    proceedBtn: HTMLElement;
}

function getWarningElements(overlayId: string, cancelId: string, proceedId: string, detailsId: string): WarningDialogElements | null {
    const overlay = document.getElementById(overlayId);
    if (!overlay) return null;
    return {
        overlay,
        detailsContainer: document.getElementById(detailsId)!,
        cancelBtn: document.getElementById(cancelId)!,
        proceedBtn: document.getElementById(proceedId)!,
    };
}

function showWarningDialog(
    elements: WarningDialogElements,
    detailsHtml: string,
): Promise<boolean> {
    return new Promise((resolve) => {
        elements.detailsContainer.innerHTML = detailsHtml;
        elements.overlay.style.display = 'flex';

        const cleanup = () => {
            elements.overlay.style.display = 'none';
            elements.cancelBtn.removeEventListener('click', onCancel);
            elements.proceedBtn.removeEventListener('click', onProceed);
        };

        const onCancel = () => {
            cleanup();
            resolve(false);
        };

        const onProceed = () => {
            cleanup();
            resolve(true);
        };

        elements.cancelBtn.addEventListener('click', onCancel);
        elements.proceedBtn.addEventListener('click', onProceed);
    });
}

/**
 * 显示编码警告对话框，返回用户是否选择继续
 */
export function showEncodingWarning(encoding: string): Promise<boolean> {
    const els = getWarningElements('encoding-warning', 'btn-encoding-cancel', 'btn-encoding-proceed', 'encoding-details');
    if (!els) return Promise.resolve(true);

    return showWarningDialog(els, `
        <div class="detail-item">Server-specified encoding: ${encoding}</div>
        <div class="detail-item">Expected: UTF-8</div>
    `);
}

/**
 * 显示 MIME 类型不匹配警告对话框，返回用户是否选择继续渲染
 *
 * @param mime 检测到的 MIME 类型
 * @param expectedMimeDescription 期望的 MIME 描述文字（如 "text/*"、"text/markdown"）
 */
export function showMimeWarning(mime: string | undefined, expectedMimeDescription: string): Promise<boolean> {
    const els = getWarningElements('mime-warning', 'btn-mime-cancel', 'btn-mime-proceed', 'mime-details');
    if (!els) return Promise.resolve(false);

    const mimeDisplay = mime || '(unspecified)';
    return showWarningDialog(els, `
        <div class="detail-item">Detected MIME type: ${mimeDisplay}</div>
        <div class="detail-item">Expected: ${expectedMimeDescription}</div>
    `);
}

/**
 * 将 ReadableStream 读取为文本字符串（同时通过 tee() 分岔用于完整性验证）
 *
 * 从 mime 参数提取 charset 作为编码，若非 UTF-8 则弹非致命警告。
 *
 * 返回 [text, verifyResult]
 */
export async function streamToTextWithVerify(
    stream: ReadableStream<Uint8Array>,
    mime: string,
    expectedHash: string,
    expectedSize: number,
): Promise<[string, VerifyResult]> {
    const [textBranch, verifyBranch] = stream.tee();

    const rawEncoding = extractEncoding(mime);
    const encoding = rawEncoding ? resolveEncoding(rawEncoding) : 'utf-8';

    if (encoding !== 'utf-8' && !isUtf8(encoding)) {
        const proceed = await showEncodingWarning(rawEncoding!);
        if (!proceed) {
            showError('Render cancelled: non-UTF-8 encoding rejected by user');
            return ['', { valid: false, actualSize: 0, actualHash: '', expectedSize, expectedHash }];
        }
    }

    const [textPromise, verifyPromise] = await Promise.all([
        (async () => {
            const reader = textBranch.getReader();
            const decoder = new TextDecoder(encoding, { fatal: false });
            let result = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                result += decoder.decode(value, { stream: true });
            }
            result += decoder.decode();
            return result;
        })(),
        verifyStream(verifyBranch, expectedHash, expectedSize),
    ]);

    return [textPromise, verifyPromise];
}
